import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { BonusTitle } from '@atlas/components';
import { NotificationManager, useApplicationState, useTranslation } from '@atlas/core';
import { Button, DefaultPopup, Label } from '@atlas/ui';

import StaticBlock from 'src/common/StaticBlock/StaticBlock';
import useFormatter from 'src/hooks/useFormatter';

import BonusCancelDetailsTable from './BonusCancelDetailsTable';

const BONUS_CANCEL_STATIC_BLOCK = 'bonus-cancel';
const BONUS_TITLE_PARAM = '[bonus_title]';

const BonusCancelPopupTitle = ({ bonusName, bonusNameHighlighted, className }) => {
  const translation = useTranslation();
  const [prefix, suffix] = useMemo(() => {
    const template = translation?.cancel_bonus_popup ?? `#cancel_bonus_popup`;
    return template.split(BONUS_TITLE_PARAM);
  }, [translation]);

  return (
    <span className={className}>
      {prefix && <span>{prefix}</span>}
      <BonusTitle name={bonusName} nameHighlighted={bonusNameHighlighted} />
      {suffix && <span>{suffix}</span>}
    </span>
  );
};

BonusCancelPopupTitle.propTypes = {
  bonusName: PropTypes.string,
  bonusNameHighlighted: PropTypes.string,
  className: PropTypes.string,
};

const BonusCancelPopup = ({ data = {}, id }) => {
  const { bonus = {}, deactivateBonus, kind } = data;
  const { wallet } = useApplicationState();
  const { currencyFormatter } = useFormatter(wallet?.currency);
  const [isLoading, setIsLoading] = useState(false);

  const bonusName = bonus?.name ?? bonus?.bonus?.name ?? '';
  const bonusNameHighlighted = bonus?.nameHighlighted ?? bonus?.bonus?.nameHighlighted ?? null;
  const { BONUS = 0 } = wallet?.detailedBalance || {};
  const forfeitTotal = Number(BONUS) || 0;
  const currency = wallet?.currency ?? '';

  const closePopupHandler = useCallback(() => {
    NotificationManager.removeNotification(id);
  }, [id]);

  const onKeepBonusHandler = useCallback(() => {
    closePopupHandler();
  }, [closePopupHandler]);

  const onCancelBonusHandler = useCallback(async () => {
    if (!deactivateBonus) return;
    setIsLoading(true);
    try {
      await deactivateBonus(bonus);
      closePopupHandler();
    } catch {
      // Error toast is handled by deactivateBonus; keep popup open for retry.
    } finally {
      setIsLoading(false);
    }
  }, [bonus, closePopupHandler, deactivateBonus]);

  const auxiliaryMessageData = useMemo(
    () => ({
      currency,
      forfeit_total: currencyFormatter.format(forfeitTotal),
    }),
    [currency, currencyFormatter, forfeitTotal],
  );

  const renderAuxiliaryLabel = useCallback(() => {
    if (kind === 'freespin')
      return (
        <Label
          className="bonus-cancel-popup__auxiliary"
          message="cancel_bonus_message_understand_freespin"
        />
      );

    return (
      <Label
        className="bonus-cancel-popup__auxiliary"
        message="cancel_bonus_message_understand"
        params={auxiliaryMessageData}
      />
    );
  }, [auxiliaryMessageData, kind]);

  return (
    <DefaultPopup className="bonus-cancel-popup" headerTitle="" onClose={closePopupHandler}>
      <div className="bonus-cancel-popup__content">
        <div className="bonus-cancel-popup__static-block">
          <StaticBlock options={{ source: BONUS_CANCEL_STATIC_BLOCK }} />
        </div>

        <BonusCancelPopupTitle
          bonusName={bonusName}
          bonusNameHighlighted={bonusNameHighlighted}
          className="bonus-cancel-popup__title"
        />
        <Label className="bonus-cancel-popup__message" message="cancel_bonus_message_undone" />
        <BonusCancelDetailsTable bonus={bonus} wallet={wallet} />
        {renderAuxiliaryLabel()}
      </div>

      <div className="bonus-cancel-popup__buttons sticky-bottom-buttons-container">
        <Button
          className="bonus-cancel-popup__button-secondary"
          disabled={isLoading}
          label="cancel_bonus_button"
          loading={isLoading}
          onClick={onCancelBonusHandler}
          size="big"
          type="secondary"
        />
        <Button
          className="bonus-cancel-popup__button-primary"
          disabled={isLoading}
          label="cancel_bonus_keep"
          onClick={onKeepBonusHandler}
          size="big"
          type="primary"
        />
      </div>
    </DefaultPopup>
  );
};

BonusCancelPopup.propTypes = {
  data: PropTypes.shape({
    bonus: PropTypes.object,
    deactivateBonus: PropTypes.func,
    kind: PropTypes.oneOf(['bonus', 'freespin']),
  }),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default BonusCancelPopup;
